import React from 'react';
import { Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { WmsProgressType } from '../../graphql/queries/getWmsProgress';
import { useTranslation } from 'react-i18next';
// 定义本页面的统计的状态类型 总数 ｜ 未打包数量 ｜ 已打包数量 ｜ 异常数量 ｜ 需退回数量
export type OrderStatus = 'total' | 'unPackNum' | 'packNum' | 'abnormalNum' | 'returnNum';

interface WmsProgressTableProps {
  progressData: WmsProgressType[];
  loading?: boolean;
  onCellClick?: (sortingCode: string, status: OrderStatus, level: string) => void;
}

interface ExpandedRowData {
  sortingCode: string;
  key: string;
  hub: string;
  whs: string;
  area: string;
  dspName?: string;
  total: number;
  unPackNum: number;
  packNum: number;
  abnormalNum: number;
  returnNum: number;
  level: string;
  children?: ExpandedRowData[];
}

export const WmsProgressTable: React.FC<WmsProgressTableProps> = ({
  progressData,
  loading,
  onCellClick
}) => {
  const expandedRows = new Set();
  const { t } = useTranslation();
  // 构建表格数据，包含展开的子级数据
  const buildTableData = (data: WmsProgressType[], level: number = 0): ExpandedRowData[] => {
    const result: ExpandedRowData[] = [];
    
    data.forEach(item => {
      const rowData: ExpandedRowData = {
        ...item,
      };
      result.push(rowData);
    });
    
    return result;
  };
  const tableData = buildTableData(progressData);

  // 创建可点击数字单元格
  const renderClickableNumber = (value: number, record: ExpandedRowData, status: OrderStatus) => {
    const displayValue = value || 0;
    
    if (displayValue === 0 || !onCellClick) {
      return <span>{displayValue}</span>;
    }

    // 根据层级构造正确的sortingCode
    const getSortingCodeForLevel = (record: ExpandedRowData): string => {
      switch (record.level) {
        case 'HUB':
          // HUB级别：直接使用sortingCode字段（已经是完整的hub代码）
          return record.sortingCode;
        case 'WHS':
          // WHS级别：从sortingCode中提取前两段，格式为 "hub-whs"
          // 由于后端WHS级别的hub字段为空，我们需要从sortingCode解析
          const parts = record.sortingCode.split('-');
          if (parts.length >= 2) {
            return `${parts[0]}-${parts[1]}`;
          } else {
            // 如果无法解析，回退到原始sortingCode
            return record.sortingCode;
          }
        case 'AREA':
          // AREA级别：直接使用sortingCode字段（已经是完整的三段码）
          return record.sortingCode;
        default:
          return record.sortingCode;
      }
    };

    return (
      <span
        className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline font-medium"
        onClick={(e) => {
          e.stopPropagation();
          const fullSortingCode = getSortingCodeForLevel(record);
          onCellClick(fullSortingCode, status, record.level);
        }}
        title={`点击查看${status === 'total' ? '全部' : status === 'unPackNum' ? '未装袋' : status === 'packNum' ? '已装袋' : status === 'abnormalNum' ? '异常' : '待退回'}订单`}
      >
        {displayValue}
      </span>
    );
  };

  const columns: TableColumnsType<ExpandedRowData> = [
    {
      title: t('wms.progress.columns.destinationHUB'),
      dataIndex: 'hub',
      key: 'hub',
      width: 120,
      align: 'left',
      render: (text, record) => {
        if (record.level === 'SUMMARY' || record.level === 'HUB') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {text}
            </div>
          );
        }
        return text || '';
      },
    },
    {
      title: t('wms.progress.columns.destinationWHS'),
      dataIndex: 'whs',
      key: 'whs',
      width: 120,
      align: 'left',
      render: (text, record) => {
        if (record.level === 'WHS') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {text}
            </div>
          );
        }
        return text || '';
      },
    },
    {
      title: t('wms.progress.columns.destinationAREA'),
      dataIndex: 'area',
      key: 'area',
      width: 120,
      align: 'left',
      render: (text) => text || '-',
    },
    {
      title: t('wms.progress.columns.assignedDSP'),
      dataIndex: 'dspName',
      key: 'dspName',
      width: 120,
      align: 'left',
      render: (text) => text || '-',
    },
    {
      title: t('wms.progress.columns.totalInHub'),
      dataIndex: 'total',
      key: 'total',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'total'),
    },
    {
      title: t('wms.progress.columns.unbagged'),
      dataIndex: 'unPackNum',
      key: 'unPackNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'unPackNum'),
    },
    {
      title: t('wms.progress.columns.bagged'),
      dataIndex: 'packNum',
      key: 'packNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'packNum'),
    },
    {
      title: t('wms.progress.columns.exception'),
      dataIndex: 'abnormalNum',
      key: 'abnormalNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'abnormalNum'),
    },
    {
      title: t('wms.progress.columns.toReturn'),
      dataIndex: 'returnNum',
      key: 'returnNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'returnNum'),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      rowKey="key"
      loading={loading}
      pagination={false}
      size="middle"
      className="mb-4"
      rowClassName={(record) => {
        if (record.level === 'SUMMARY') return 'bg-blue-50 font-bold';
        if (record.level === 'AREA') return 'bg-gray-50';
        return '';
      }}
      expandable={{
        defaultExpandAllRows: false,
        childrenColumnName: 'children',
        rowExpandable: (record) => record.level !== 'SUMMARY' && !!record.children && record.children.length > 0
      }}
    />
  );
}; 